// components
import { Grid } from '@interstate/components/Grid';
import { NumericInput } from '@interstate/components/NumericInput';
import { useState } from 'react';
import { createPriceValidationOnBlurHandler } from '../../../../utils';

import { getCompactGridBreakpoints } from './getCompactGridBreakpoints';

export type VehicleRowDataInputProps = {
    onSellingPriceChange: (value: string) => void;
    onOdometerChange: (value: string) => void;
    onMsrpChange: (value: string) => void;
    msrp?: string;
    odometer: string;
    minOdometerValue: number;
    sellingPrice: string;
    vin: string;
    isMsrpRequired: boolean;
    forceSmallBreakpoint?: boolean;
    autoFocus?: boolean;
};

const toNumber = (value?: string) => (value?.trim() ? Number(value.replace(/,/g, '')) : NaN);

const getFocusedField = (
    { msrp, sellingPrice, odometer }: Record<string, string | undefined>,
    isMsrpRequired: boolean,
    minOdometerValue: number
) => {
    if (isMsrpRequired && !(toNumber(msrp) > 0)) return 'msrp';
    if (!(toNumber(sellingPrice) > 0)) return 'sellingPrice';
    if (!(toNumber(odometer) >= minOdometerValue)) return 'odometer';
    return false;
};

export const EditVehicle = (props: VehicleRowDataInputProps) => {
    const {
        odometer,
        sellingPrice,
        msrp,
        onOdometerChange,
        onSellingPriceChange,
        onMsrpChange,
        minOdometerValue,
        vin,
        isMsrpRequired,
        autoFocus
    } = props;

    const focusedField = autoFocus && getFocusedField({ msrp, sellingPrice, odometer }, isMsrpRequired, minOdometerValue);

    const [msrpError, setMsrpError] = useState<string>('');
    const [sellingPriceError, setSellingPriceError] = useState<string>('');

    return (
        <>
            <Grid
                container
                columnSpacing={2}
                marginTop={2}
                sx={{ '& .ids-input-component-label': { whiteSpace: 'nowrap!important' } }}
            >
                <Grid {...getCompactGridBreakpoints({ xs: 12, md: 4, forceSmallBreakpoint: props.forceSmallBreakpoint })}>
                    <NumericInput
                        placeholder="0.00"
                        name="MSRP"
                        label={isMsrpRequired ? 'MSRP' : 'Retail Price'}
                        value={msrp}
                        onChange={(e) => onMsrpChange(e.target.value)}
                        onBlur={createPriceValidationOnBlurHandler(
                            isMsrpRequired ? 'MSRP' : 'Retail Price',
                            msrp || '',
                            onMsrpChange,
                            setMsrpError,
                            isMsrpRequired
                        )}
                        data-testid={isMsrpRequired ? `custom-msrp-${vin}` : `custom-retail-price-${vin}`}
                        autoFocus={focusedField === 'msrp'}
                        required={isMsrpRequired}
                        allowDecimal
                        decimalMaxLength={2}
                        decimalMinLength={0}
                        inputPrefix={'$'}
                        autoInsertCommas
                        errorMessage={msrpError}
                        hasError={!!msrpError}
                    />
                </Grid>
                <Grid {...getCompactGridBreakpoints({ xs: 12, md: 4, forceSmallBreakpoint: props.forceSmallBreakpoint })}>
                    <NumericInput
                        placeholder="0.00"
                        name="Selling Price"
                        label="Selling Price"
                        value={sellingPrice}
                        onChange={(e) => onSellingPriceChange(e.target.value)}
                        onBlur={createPriceValidationOnBlurHandler(
                            'Selling Price',
                            sellingPrice,
                            onSellingPriceChange,
                            setSellingPriceError,
                            true
                        )}
                        data-testid={`custom-selling-price-${vin}`}
                        autoFocus={focusedField === 'sellingPrice'}
                        required
                        allowDecimal
                        decimalMaxLength={2}
                        decimalMinLength={0}
                        inputPrefix={'$'}
                        autoInsertCommas
                        errorMessage={sellingPriceError}
                        hasError={!!sellingPriceError}
                    />
                </Grid>
                <Grid {...getCompactGridBreakpoints({ xs: 12, md: 4, forceSmallBreakpoint: props.forceSmallBreakpoint })}>
                    <NumericInput
                        placeholder="0"
                        name="Odometer"
                        label={'Odometer'}
                        value={odometer || '0'}
                        onChange={(e) => onOdometerChange(e.target.value)}
                        data-testid={`custom-odometer-${vin}`}
                        autoFocus={focusedField === 'odometer'}
                        required
                        allowDecimal={false}
                        autoInsertCommas
                        minValue={minOdometerValue}
                        customErrorMessages={{
                            lessThanMinValue: 'The value should be greater than 0.'
                        }}
                    />
                </Grid>
            </Grid>
        </>
    );
};
